import { useState, useCallback, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"

import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { readActivityOverviewList, readActivityDetail } from "@/app/api/activity"
import { readPdfTemplateDetail } from "@/app/api/pdfTemplate"
import { resolvePeriodDates } from "@/utils/filterPreset"
import { createGlobalStore } from "@/stores/global"
import { exportPagesToPdf } from "@/app/report/pdf/builder/utils/captureReportPdf"
import { applyPdfTemplateContent } from "@/app/report/pdf/builder/utils/applyTemplateContent"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import type { FilterPreset } from "@/types/filterPreset"
import type { PdfTemplateContent } from "@/types/pdfTemplate"
import type { PdfBuilderState } from "@/types/pdfBuilder"

type Phase = "idle" | "preparing" | "rendering" | "exporting"

/**
 * PDF Template 기반 Quick Export 훅.
 *
 * startExport: 버튼 클릭 → store 세팅 → 오프스크린 렌더 → 데이터 로딩 대기 → PDF 생성 → 다운로드
 * onDataReady: 오프스크린 PdfDataProvider가 로딩 완료 시 호출 → 자동으로 캡처 시작
 */
export function useQuickPdfExport() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setSnackBar } = createGlobalStore()

  const [phase, setPhase] = useState<Phase>("idle")
  const [activityName, setActivityName] = useState("")

  const snapshotRef = useRef<PdfBuilderState | null>(null)
  const isMountedRef = useRef(true)

  // 오프스크린 캔버스를 보여줄지 (preparing/rendering/exporting 중)
  const isActive = phase !== "idle"
  // 버튼 로딩 표시용
  const isBusy = phase !== "idle"

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (snapshotRef.current) {
        usePdfBuilderStore.setState(snapshotRef.current)
        snapshotRef.current = null
      }
    }
  }, [])

  const cleanup = useCallback(() => {
    if (snapshotRef.current) {
      usePdfBuilderStore.setState(snapshotRef.current)
      snapshotRef.current = null
    }
    setPhase("idle")
    setActivityName("")
  }, [])

  /**
   * 데이터 로딩 완료 시 오프스크린 컴포넌트가 호출.
   * rendering → exporting 전환 후 PDF 생성.
   */
  const onDataReady = useCallback(
    async (ready: boolean) => {
      if (!ready || phase !== "rendering") return

      setPhase("exporting")

      try {
        const filename = activityName.replace(/[^a-zA-Z0-9가-힣\s_-]/g, "_") || "report"

        await exportPagesToPdf({
          filename,
          quality: "high",
          includePageNumbers: true,
        })
      } catch (error) {
        console.error("Quick PDF export failed:", error)
        if (isMountedRef.current) {
          setSnackBar({
            open: true,
            message: t("exportFailed", "PDF export failed. Please try again."),
            severity: "error",
          })
        }
      } finally {
        if (isMountedRef.current) {
          cleanup()
        }
      }
    },
    [phase, activityName, setSnackBar, t, cleanup],
  )

  /**
   * PDF 버튼 클릭 → 전체 플로우 시작
   */
  const startExport = useCallback(
    async (preset: FilterPreset) => {
      if (!preset.idfPdfTemplate) return

      setPhase("preparing")
      setActivityName("")

      try {
        // 1. Snapshot current store state
        const currentState = usePdfBuilderStore.getState()
        // 상태 필드를 하나씩 베끼면 필드가 늘 때마다 여기도 고쳐야 한다 — 통째로 떠 둔다 (동작은 같다)
        snapshotRef.current = { ...currentState }

        // 2. Reset store
        currentState.reset()

        // 3. Resolve dates & fetch activities
        const { dateStart, dateFinish } = resolvePeriodDates(
          preset.periodType,
          preset.customDateStart,
          preset.customDateEnd,
        )

        const activitiesRes = await readActivityOverviewList({
          start: new Date(dateStart).valueOf(),
          end: new Date(dateFinish).valueOf(),
          ...(preset.activityTypes.length > 0 && { type: preset.activityTypes.join(",") }),
        })

        const activityList = (activitiesRes as any)?.response ?? activitiesRes ?? []
        const analyzed = activityList.filter(
          (a: any) => a.status === "ANALYZED" || a.analyzeFinished,
        )
        const sorted = [...analyzed].sort(
          (a: any, b: any) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime(),
        )

        if (sorted.length === 0) {
          setSnackBar({
            open: true,
            message: t(
              "noAnalyzedActivity",
              "No analyzed activities found for the selected filters.",
            ),
            severity: "warning",
          })
          cleanup()
          return
        }

        // 4. Fetch PDF template detail & apply to store
        const templateResult = await queryClient.fetchQuery({
          queryKey: ["readPdfTemplateDetail", preset.idfPdfTemplate],
          queryFn: () => readPdfTemplateDetail(preset.idfPdfTemplate!),
        })
        const templateDetail = (templateResult as any)?.response ?? templateResult
        if (!templateDetail?.content) {
          throw new Error("Template content is empty")
        }

        const templateContent: PdfTemplateContent = JSON.parse(templateDetail.content)

        // 5. Apply template pages to store (여러 페이지 템플릿은 한 페이지에 이어 붙임 — 편집 화면과 같은 함수)
        applyPdfTemplateContent(templateContent)

        // 6. Set filter: activity
        const firstActivity = sorted[0]
        usePdfBuilderStore.getState().setFilterActivity(firstActivity.idfActivity)
        setActivityName(firstActivity.name ?? "report")

        // 7. Fetch activity detail for session IDs
        const activityDetailRes = await readActivityDetail(firstActivity.idfActivity)
        const activityDetail = (activityDetailRes as any)?.response ?? activityDetailRes
        if (activityDetail?.sessions?.length > 0) {
          const sessionIds = activityDetail.sessions.map((s: any) => s.idfSession)
          usePdfBuilderStore.getState().setFilterSessions(sessionIds)
        }
        // 리포트 화면의 기본 선택과 같게 — 세션이 있으면 세션 모드, 없으면 액티비티 모드.
        // 섹션 설정은 프리셋의 리포트 템플릿을 따른다(없으면 최근 사용 템플릿) (#75)
        usePdfBuilderStore
          .getState()
          .setFilterReportMode(
            activityDetail?.sessions?.length > 0
              ? ACTIVITY_SESSION_TYPE.SESSION
              : ACTIVITY_SESSION_TYPE.ACTIVITY,
          )
        usePdfBuilderStore.getState().setFilterReportTemplate(preset.idfTemplate ?? null)

        // 8. Set filter: players
        if (preset.selectedPlayerIds.length > 0) {
          usePdfBuilderStore.getState().setFilterPlayers(preset.selectedPlayerIds)
        }

        if (!isMountedRef.current) {
          cleanup()
          return
        }

        // 9. → rendering phase: 오프스크린 캔버스가 마운트되고 데이터 로딩 시작
        //    PdfDataProvider가 로딩 완료하면 onDataReady 콜백으로 exporting 전환
        setPhase("rendering")
      } catch (error) {
        console.error("Quick PDF export preparation failed:", error)
        if (isMountedRef.current) {
          setSnackBar({
            open: true,
            message: t("exportFailed", "PDF export failed. Please try again."),
            severity: "error",
          })
        }
        cleanup()
      }
    },
    [queryClient, t, setSnackBar, cleanup],
  )

  return {
    startExport,
    onDataReady,
    cleanup,
    isActive,
    isBusy,
    phase,
  }
}
